/**
 * DesignedScrollArea — hides the native scrollbar and renders the canonical
 * custom design (an 18px strip with a `dinnerplate-white` hairline, a 6px
 * dinnerplate-white pill thumb 5px off each side, and a grey-light hover) as an
 * overlay synced to the content's scroll position. The thumb is draggable;
 * wheel + trackpad use the native scroll underneath.
 *
 * Used in production by `DataTableContent` (the vertical lane starts below
 * the sticky header via `vScrollbarTop`, with the `vLaneBorder` divider),
 * `CancelOrderModal` (the reasons list), and by stories to pixel-match Figma
 * overlays (`scrollbar.stories.tsx`, `searchable-dropdown.stories.tsx`).
 */

// React
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';

// Hooks and utilities
import { cn } from '@/lib/utils';

const LANE_PADDING = 5;
const THUMB_THICKNESS = 6;
// Track inset at each travel end of a lane: just the padding — the lane
// boxes carry no border on the travel ends (V: top/bottom, H: left/right),
// so the thumb rests exactly LANE_PADDING from each end.
const SUBTRACT = 2 * LANE_PADDING; // 10
const MIN_THUMB_SIZE = 40;
const KEYBOARD_SCROLL_STEP = 40;
/**
 * Cross-axis size of the lane BOX. The design primitive is an 18px strip closed
 * by a 1px stroke on BOTH sides — 5px, 6px thumb, 5px between them. We draw
 * only the near stroke (`border-l` / `border-t`) because the far side is the
 * enclosing card's own border, so the box is one pixel narrower and the thumb
 * still sits 5px off each stroke.
 */
const LANE_BOX = 1 + LANE_PADDING + THUMB_THICKNESS + LANE_PADDING; // 17
/**
 * Width of the visible scrollbar strip, counting the container border that
 * closes it. Exported so callers can reserve it as a content gutter — the lanes
 * are overlays, so anything at the container's edge sits under them otherwise.
 */
export const SCROLLBAR_LANE_SIZE = LANE_BOX + 1; // 18

export function DesignedScrollArea({
  children,
  axis = 'y',
  className,
  contentClassName,
  contentBg = 'bg-off-white',
  vScrollbarTop = 0,
  vLaneBorder = false,
  scrollbarLabel = 'Scrollable content',
  scrollRef,
  style,
}: {
  children: React.ReactNode;
  axis?: 'y' | 'x' | 'both';
  className?: string;
  contentClassName?: string;
  contentBg?: string;
  /**
   * Top inset (px) for the vertical scrollbar lane. Pass the sticky header
   * height so the V lane starts BELOW the header instead of spanning over it
   * (47 for the Table story's header strip).
   */
  vScrollbarTop?: number;
  /**
   * Draw a 1px divider on the left edge of the V lane (matches the Figma
   * scrollbar primitive's stroke and the DataTable border weight). Off by
   * default to avoid a double-line look on small bordered cards (e.g. the
   * Scrollbar/DropdownMenu story).
   */
  vLaneBorder?: boolean;
  /** Accessible name prefix for the vertical and horizontal scrollbars. */
  scrollbarLabel?: string;
  /**
   * Exposes the scrolling element to the parent (virtualizers, keyboard
   * navigation, external scroll control). Called with the node on mount and
   * `null` on unmount.
   */
  scrollRef?: (node: HTMLDivElement | null) => void;
  style?: React.CSSProperties;
}) {
  const viewportId = useId();
  const contentRef = useRef<HTMLDivElement>(null);
  const [vState, setVState] = useState({
    top: 0,
    height: 0,
    show: false,
    range: 0,
    value: 0,
    max: 0,
  });
  const [hState, setHState] = useState({
    left: 0,
    width: 0,
    show: false,
    range: 0,
    value: 0,
    max: 0,
  });
  const measuredHVisibilityRef = useRef(false);
  const vDragRef = useRef<{ startY: number; startScrollTop: number; ratio: number } | null>(null);
  const hDragRef = useRef<{ startX: number; startScrollLeft: number; ratio: number } | null>(null);

  const recalc = useCallback(() => {
    const el = contentRef.current;
    if (!el) return;
    const { scrollTop, scrollLeft, scrollHeight, scrollWidth, clientHeight, clientWidth } = el;
    const hVisible = (axis === 'x' || axis === 'both') && scrollWidth > clientWidth + 1;
    if (axis === 'y' || axis === 'both') {
      // The V lane box is shorter than the scroll container: inset from the
      // top by vScrollbarTop (sticky header) and from the bottom by the H
      // lane when it shows — the thumb travel must use the lane's real
      // length or it overshoots the lane at full scroll.
      const lane = Math.max(0, clientHeight - vScrollbarTop - (hVisible ? LANE_BOX : 0) - SUBTRACT);
      if (scrollHeight > clientHeight + 1) {
        // Cap at the lane: in a container short enough that the lane is under
        // MIN_THUMB_SIZE, the minimum would overhang the lane end and leave a
        // negative travel range, which also kills thumb dragging.
        const thumbH = Math.min(lane, Math.max(MIN_THUMB_SIZE, (clientHeight / scrollHeight) * lane));
        const range = lane - thumbH;
        const scrollable = scrollHeight - clientHeight;
        const top = scrollable > 0 ? (scrollTop / scrollable) * range : 0;
        setVState({ top, height: thumbH, show: true, range, value: scrollTop, max: scrollable });
      } else {
        setVState({ top: 0, height: 0, show: false, range: 0, value: 0, max: 0 });
      }
    }
    if (axis === 'x' || axis === 'both') {
      const lane = Math.max(0, clientWidth - SUBTRACT);
      if (hVisible) {
        const thumbW = Math.min(lane, Math.max(MIN_THUMB_SIZE, (clientWidth / scrollWidth) * lane));
        const range = lane - thumbW;
        const scrollable = scrollWidth - clientWidth;
        const left = scrollable > 0 ? (scrollLeft / scrollable) * range : 0;
        setHState({ left, width: thumbW, show: true, range, value: scrollLeft, max: scrollable });
      } else {
        setHState({ left: 0, width: 0, show: false, range: 0, value: 0, max: 0 });
      }
    }
  }, [axis, vScrollbarTop]);

  useLayoutEffect(() => {
    if (measuredHVisibilityRef.current === hState.show) return;
    measuredHVisibilityRef.current = hState.show;
    // Re-measure once the conditional gutter has entered or left layout so
    // the vertical thumb and ARIA range include the browser's real scrollHeight.
    recalc();
  }, [hState.show, recalc]);

  useEffect(() => {
    recalc();
    const el = contentRef.current;
    if (!el) return;
    el.addEventListener('scroll', recalc);
    const ro = new ResizeObserver(recalc);
    ro.observe(el);
    // Also observe the content itself: scrollHeight changes (rows loading in,
    // filters emptying the table) don't resize the container, so observing
    // only `el` would leave the thumb stale until the next scroll.
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => {
      el.removeEventListener('scroll', recalc);
      ro.disconnect();
    };
  }, [recalc]);

  useEffect(() => {
    const handleMove = (event: PointerEvent) => {
      const el = contentRef.current;
      if (!el) return;
      if (vDragRef.current) {
        const { startY, startScrollTop, ratio } = vDragRef.current;
        const scrollable = el.scrollHeight - el.clientHeight;
        el.scrollTop = Math.max(0, Math.min(scrollable, startScrollTop + (event.clientY - startY) * ratio));
      }
      if (hDragRef.current) {
        const { startX, startScrollLeft, ratio } = hDragRef.current;
        const scrollable = el.scrollWidth - el.clientWidth;
        el.scrollLeft = Math.max(0, Math.min(scrollable, startScrollLeft + (event.clientX - startX) * ratio));
      }
    };
    const handleUp = () => {
      vDragRef.current = null;
      hDragRef.current = null;
    };
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleUp);
    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleUp);
    };
  }, []);

  const onVThumbDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.focus({ preventScroll: true });
    event.preventDefault();
    const el = contentRef.current;
    if (!el || vState.range <= 0) return;
    const scrollable = el.scrollHeight - el.clientHeight;
    vDragRef.current = {
      startY: event.clientY,
      startScrollTop: el.scrollTop,
      ratio: scrollable / vState.range,
    };
  };

  const onHThumbDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.focus({ preventScroll: true });
    event.preventDefault();
    const el = contentRef.current;
    if (!el || hState.range <= 0) return;
    const scrollable = el.scrollWidth - el.clientWidth;
    hDragRef.current = {
      startX: event.clientX,
      startScrollLeft: el.scrollLeft,
      ratio: scrollable / hState.range,
    };
  };

  const setContentRef = useCallback(
    (node: HTMLDivElement | null) => {
      contentRef.current = node;
      scrollRef?.(node);
    },
    [scrollRef],
  );

  const onScrollbarKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>,
    orientation: 'vertical' | 'horizontal',
  ) => {
    const el = contentRef.current;
    if (!el) return;

    const vertical = orientation === 'vertical';
    const current = vertical ? el.scrollTop : el.scrollLeft;
    const max = Math.max(
      0,
      vertical ? el.scrollHeight - el.clientHeight : el.scrollWidth - el.clientWidth,
    );
    const page = vertical
      ? Math.max(KEYBOARD_SCROLL_STEP, el.clientHeight - vScrollbarTop)
      : Math.max(KEYBOARD_SCROLL_STEP, el.clientWidth);
    let next: number;

    switch (event.key) {
      case 'ArrowDown':
        if (!vertical) return;
        next = current + KEYBOARD_SCROLL_STEP;
        break;
      case 'ArrowUp':
        if (!vertical) return;
        next = current - KEYBOARD_SCROLL_STEP;
        break;
      case 'ArrowRight':
        if (vertical) return;
        next = current + KEYBOARD_SCROLL_STEP;
        break;
      case 'ArrowLeft':
        if (vertical) return;
        next = current - KEYBOARD_SCROLL_STEP;
        break;
      case 'PageDown':
        next = current + page;
        break;
      case 'PageUp':
        next = current - page;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = max;
        break;
      default:
        return;
    }

    event.preventDefault();
    // DataTable has document-level row-navigation shortcuts for the same keys.
    event.stopPropagation();
    const clamped = Math.max(0, Math.min(max, next));
    if (vertical) el.scrollTop = clamped;
    else el.scrollLeft = clamped;
  };

  return (
    <div className={cn('relative', className)} style={style}>
      <div
        id={viewportId}
        ref={setContentRef}
        // The horizontal lane is an overlay. Matching the full visible strip
        // (lane box plus its closing border) extends the native vertical scroll
        // range enough to keep fractional content edges above the lane.
        style={{ paddingBottom: hState.show ? SCROLLBAR_LANE_SIZE : 0 }}
        className={cn(
          // overscroll-none kills the rubber-band bounce: the lanes are
          // pinned overlays, so elastic overscroll would slide the content
          // (sticky header included) out from under them.
          'h-full w-full overflow-auto overscroll-none [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden [&::-webkit-scrollbar]:size-0 [&::-webkit-scrollbar]:appearance-none',
          contentClassName,
        )}
      >
        {children}
      </div>
      {vState.show && (
        <div
          // Inline `top`/`bottom` so the lane reliably (a) starts BELOW any
          // sticky header (`vScrollbarTop`) and (b) stops ABOVE the horizontal
          // scrollbar when both axes are visible. `z-20` keeps the lane above
          // any sticky cells inside the scrolling content (e.g. the Table's
          // More column at z-10).
          // Without the divider there is no near stroke to inset from, so the
          // box loses that pixel too and the thumb keeps its 5px on both sides.
          style={{
            top: vScrollbarTop,
            bottom: hState.show ? LANE_BOX : 0,
            width: vLaneBorder ? LANE_BOX : LANE_BOX - 1,
          }}
          className={cn(
            'absolute right-0 p-[5px] pointer-events-none z-20',
            vLaneBorder && 'border-l border-dinnerplate-white',
            contentBg,
          )}
        >
          <div
            role="scrollbar"
            tabIndex={0}
            aria-controls={viewportId}
            aria-label={`${scrollbarLabel} vertical scrollbar`}
            aria-orientation="vertical"
            aria-valuemin={0}
            aria-valuemax={Math.round(vState.max)}
            aria-valuenow={Math.round(vState.value)}
            onKeyDown={(event) => onScrollbarKeyDown(event, 'vertical')}
            onPointerDown={onVThumbDown}
            style={{
              position: 'absolute',
              top: vState.top + LANE_PADDING,
              height: vState.height,
              left: LANE_PADDING,
              width: THUMB_THICKNESS,
            }}
            className="rounded-[100px] bg-dinnerplate-white hover:bg-grey-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-off-black transition-colors cursor-grab active:cursor-grabbing select-none pointer-events-auto"
          />
        </div>
      )}
      {hState.show && (
        <div
          style={{ height: LANE_BOX }}
          className={cn(
            'absolute bottom-0 left-0 right-0 border-t border-dinnerplate-white p-[5px] pointer-events-none z-20',
            contentBg,
          )}
        >
          <div
            role="scrollbar"
            tabIndex={0}
            aria-controls={viewportId}
            aria-label={`${scrollbarLabel} horizontal scrollbar`}
            aria-orientation="horizontal"
            aria-valuemin={0}
            aria-valuemax={Math.round(hState.max)}
            aria-valuenow={Math.round(hState.value)}
            onKeyDown={(event) => onScrollbarKeyDown(event, 'horizontal')}
            onPointerDown={onHThumbDown}
            style={{
              position: 'absolute',
              left: hState.left + LANE_PADDING,
              width: hState.width,
              top: LANE_PADDING,
              height: THUMB_THICKNESS,
            }}
            className="rounded-[100px] bg-dinnerplate-white hover:bg-grey-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-off-black transition-colors cursor-grab active:cursor-grabbing select-none pointer-events-auto"
          />
        </div>
      )}
    </div>
  );
}
